/**
 * Temperature-log data layer — the chef day workflow's REST surface.
 *
 *   - `useTempLogDaysQuery()`            — GET /api/temp-log/days: the
 *       actor's open temp-log DAYS (per-shipment CHEF_SUBMIT_TEMP_LOG
 *       tasks grouped server-side by their shipment's serviceDate).
 *       Feeds the synthesized one-row-per-day inbox entries.
 *   - `useTempLogDayContextQuery(date)`  — GET /api/temp-log/day-context:
 *       locations → mealtimes → deliverables for one day, with
 *       per-location confirmed state + previously-saved readings.
 *   - `useConfirmTempLogLocationMutation()` — POST /api/temp-log/
 *       confirm-location: one transactional call per location confirm —
 *       the server writes ShipmentEvent + ProductionRecordTemperatureLog
 *       rows and completes every task at that location, so there is no
 *       client-side partial-failure orchestration.
 *
 * The wire shapes mirror `apps/ordo/server/src/managers/temp-log.ts`.
 * Projection into the workflow page's `TempLogDayContext` view shape
 * lives in `lib/chefs/temp-log.ts`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from './http'

// ---------------------------------------------------------------------------
// Wire types (server contract)
// ---------------------------------------------------------------------------

export interface TempLogDaySummaryWire {
  /** `YYYY-MM-DD` service day. */
  dayKey: string
  /** ISO timestamp — earliest dueAt across the day's open tasks. */
  dueAt: string | null
  openTaskCount: number
  totalTaskCount: number
}

export interface TempLogDaysResponse {
  days: TempLogDaySummaryWire[]
}

export interface TempLogContextTaskWire {
  taskId: string
  shipmentId: string
  mealtime: string
  status: string
}

export interface TempLogContextDeliverableWire {
  /** Server-side deliverable id — opaque to the client. */
  id: string
  name: string
  plannedQty: number
  /** `ambient` = shelf-stable (Neutral); `unknown` = class not derivable, ask for a reading. */
  temperature: 'hot' | 'chilled' | 'ambient' | 'unknown'
  savedKitchenF: number | null
  savedDropOffF: number | null
}

export interface TempLogContextMealtimeWire {
  /** Server enum: BREAKFAST | LUNCH | SNACK | SUPPER. */
  mealtime: string
  label: string
  mealCount: number
  deliverables: TempLogContextDeliverableWire[]
}

export interface TempLogContextLocationWire {
  id: string
  name: string
  organizationName: string
  address: string
  deliveryWindow: string
  confirmed: boolean
  tasks: TempLogContextTaskWire[]
  mealtimes: TempLogContextMealtimeWire[]
}

export interface TempLogDayContextResponse {
  date: string
  locations: TempLogContextLocationWire[]
}

export interface ConfirmTempLogLocationVariables {
  /** `YYYY-MM-DD` service day. */
  date: string
  locationId: string
  readings: Array<{
    componentId: string
    kitchenTemperatureF: number | null
    dropOffTemperatureF: number | null
  }>
  notes?: string | null
}

export interface ConfirmTempLogLocationResponse {
  confirmedTaskIds: string[]
  readingsWritten: number
  eventsWritten: number
}

export interface SubmitTempLogDayVariables {
  /** `YYYY-MM-DD` service day. */
  date: string
  locations: Array<{
    locationId: string
    readings: Array<{
      componentId: string
      kitchenTemperatureF: number | null
      dropOffTemperatureF: number | null
    }>
  }>
  notes?: string | null
}

export interface SubmitTempLogDayResponse {
  confirmedTaskIds: string[]
  readingsWritten: number
  eventsWritten: number
  locationsSubmitted: number
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const tempLogDaysQueryKey = ['temp-log', 'days'] as const
export const tempLogDayContextQueryKey = (date: string) => ['temp-log', 'day-context', date] as const

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

export async function fetchTempLogDays(signal?: AbortSignal): Promise<TempLogDaysResponse> {
  return apiFetch<TempLogDaysResponse>('/api/temp-log/days', { signal })
}

export async function fetchTempLogDayContext(date: string, signal?: AbortSignal): Promise<TempLogDayContextResponse> {
  return apiFetch<TempLogDayContextResponse>('/api/temp-log/day-context', { query: { date }, signal })
}

export async function confirmTempLogLocation(
  variables: ConfirmTempLogLocationVariables,
): Promise<ConfirmTempLogLocationResponse> {
  return apiFetch<ConfirmTempLogLocationResponse>('/api/temp-log/confirm-location', {
    method: 'POST',
    body: variables,
  })
}

export async function submitTempLogDay(variables: SubmitTempLogDayVariables): Promise<SubmitTempLogDayResponse> {
  return apiFetch<SubmitTempLogDayResponse>('/api/temp-log/submit-day', {
    method: 'POST',
    body: variables,
  })
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Polls every 30s — matches the tasks-inbox cadence so the day rows
 * track cross-tab confirms / the day rolling over. */
export function useTempLogDaysQuery(enabled = true) {
  return useQuery<TempLogDaysResponse>({
    queryKey: tempLogDaysQueryKey,
    queryFn: ({ signal }) => fetchTempLogDays(signal),
    refetchInterval: 30_000,
    enabled,
  })
}

export function useTempLogDayContextQuery(date: string | null) {
  return useQuery<TempLogDayContextResponse>({
    queryKey: tempLogDayContextQueryKey(date ?? ''),
    queryFn: ({ signal }) => fetchTempLogDayContext(date ?? '', signal),
    enabled: date !== null && date !== '',
  })
}

export function useConfirmTempLogLocationMutation() {
  const queryClient = useQueryClient()
  return useMutation<ConfirmTempLogLocationResponse, Error, ConfirmTempLogLocationVariables>({
    mutationFn: confirmTempLogLocation,
    onSettled: (_data, _error, variables) => {
      // Day rows, the day's context (confirmed flags + readback), the
      // inbox + counts, and the operator-task sheets all reflect task
      // completion — refetch the lot.
      void queryClient.invalidateQueries({ queryKey: tempLogDaysQueryKey })
      void queryClient.invalidateQueries({ queryKey: tempLogDayContextQueryKey(variables.date) })
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      void queryClient.invalidateQueries({ queryKey: ['operator-tasks'] })
    },
  })
}

/** Submit the whole day in one atomic call — writes every location's
 * readings and completes every temp-log task. */
export function useSubmitTempLogDayMutation() {
  const queryClient = useQueryClient()
  return useMutation<SubmitTempLogDayResponse, Error, SubmitTempLogDayVariables>({
    mutationFn: submitTempLogDay,
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: tempLogDaysQueryKey })
      void queryClient.invalidateQueries({ queryKey: tempLogDayContextQueryKey(variables.date) })
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      void queryClient.invalidateQueries({ queryKey: ['operator-tasks'] })
    },
  })
}
